<script setup lang="ts">
import { getDisplayNameStyle, getGummyLetters } from '~/utils/discordProfile'
import type { DiscordDisplayNameStyles } from '~/utils/discordProfile'

// Discord display name styles (font + effect + colors). The effect CSS below
// mirrors Discord's client; animations play once, like when a profile opens.
const props = defineProps<{
  name: string
  styles: DiscordDisplayNameStyles | null
  /** Run the effect animation once; call `replay()` to run it again */
  animated?: boolean
}>()

const style = computed(() => props.styles ? getDisplayNameStyle(props.styles, props.name) : null)

const gummyLetters = computed(() => {
  if (style.value?.effect !== 'gummy') return []
  return getGummyLetters(props.name, style.value.colors)
})

const rootRef = ref<HTMLElement | null>(null)

// Restarts the effect without re-rendering (a parent re-render would re-apply
// the card's inline entrance animation)
function replay() {
  for (const animation of rootRef.value?.getAnimations({ subtree: true }) ?? []) {
    animation.cancel()
    animation.play()
  }
}

defineExpose({ replay })
</script>

<template>
  <span
    v-if="style"
    ref="rootRef"
    class="dns"
    :class="[style.fontClass, { 'dns-animated': animated }]"
    :style="style.vars"
  >
    <span
      class="dns-inner"
      :class="`dns-${style.effect}`"
      :data-text="name"
    >
      <template v-if="style.effect === 'gummy'">
        <span
          v-for="(letter, i) in gummyLetters"
          :key="i"
          :class="{ 'dns-gummy-letter': letter.index !== undefined }"
          :style="letter.index !== undefined ? { 'color': letter.color, '--dns-letter-index': letter.index } : undefined"
        >{{ letter.text }}</span>
      </template>
      <template v-else>{{ name }}</template>
    </span>
  </span>
  <span
    v-else
    class="min-w-0 truncate"
  >{{ name }}</span>
</template>

<style scoped>
.dns {
  display: inline-flex;
  max-width: 100%;
  min-width: 0;
  position: relative;
  z-index: 0;
  font-synthesis: none;
}

.dns-inner {
  flex: 1 1 0%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Fonts: one weight each, like Discord. Néo-Castel (Medieval) and Sinistre
   (Vampyre) are not on Google Fonts, so Jacquard 24 and Macondo stand in. */
.dns-font-3 { font-family: 'Cherry Bomb One', sans-serif; font-weight: 400; letter-spacing: 0.04em; }
.dns-font-4 { font-family: 'Chicle', sans-serif; font-weight: 400; }
.dns-font-6 { font-family: 'MuseoModerno', sans-serif; font-weight: 500; letter-spacing: 0.01em; }
.dns-font-7 { font-family: 'Jacquard 24', sans-serif; font-weight: 400; letter-spacing: 0.02em; }
.dns-font-8 { font-family: 'Pixelify Sans', sans-serif; font-weight: 400; letter-spacing: 0.02em; }
.dns-font-10 { font-family: 'Macondo', sans-serif; font-weight: 400; letter-spacing: 0.01em; }
.dns-font-12 { font-family: 'Zilla Slab', sans-serif; font-weight: 600; letter-spacing: 0.03em; }
.dns-font-13 { font-family: 'Playpen Sans', sans-serif; font-weight: 700; }
.dns-font-14 { font-family: 'Orbitron', sans-serif; font-weight: 700; letter-spacing: 0.03em; }
.dns-font-15 { font-family: 'New Rocker', sans-serif; font-weight: 400; }
.dns-font-16 { font-family: 'Kalam', sans-serif; font-weight: 700; }

.dns-solid {
  color: var(--dns-main);
}

.dns-gradient,
.dns-prism {
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  z-index: 0;
}

.dns-gradient {
  background-image: linear-gradient(to bottom right, var(--dns-gradient-stops));
  background-size: 100% auto;
}

.dns-prism {
  background-image: linear-gradient(90deg, var(--dns-prism-stops));
  background-position-x: 0;
  background-repeat: repeat;
  background-size: var(--dns-prism-cycle) auto;
}

.dns-neon {
  --dns-neon-radius: calc(4px + 0.12em);
  paint-order: stroke fill;
  -webkit-text-stroke-width: calc(1px + 0.04em);
  -webkit-text-stroke-color: var(--dns-neon-stroke);
  color: #fff;
  margin: calc(var(--dns-neon-radius) * -1);
  padding: var(--dns-neon-radius);
  position: relative;
  text-shadow: 0 0 var(--dns-neon-radius) var(--dns-main);
  z-index: 0;
}

.dns-toon {
  --dns-toon-width: calc(1.6px + 0.04em);
  --dns-toon-margin: calc(var(--dns-toon-width) * -1);
  paint-order: stroke fill;
  position: relative;
  -webkit-text-stroke-width: var(--dns-toon-width);
  -webkit-text-stroke-color: var(--dns-toon-stroke-color);
  color: var(--dns-toon-stroke-color);
  margin-inline: var(--dns-toon-margin);
  margin-bottom: var(--dns-toon-margin);
  padding-inline: var(--dns-toon-width);
  padding-bottom: var(--dns-toon-width);
}

.dns-toon::before {
  content: attr(data-text);
  position: absolute;
  inset: 0;
  padding-inline: var(--dns-toon-width);
  background: linear-gradient(180deg, #fff 0, var(--dns-light-2) 8%, var(--dns-light-1) 15%, var(--dns-main) 25%, var(--dns-light-2) 45%, var(--dns-main) 55%, #fff 75%, var(--dns-light-2) 83%, var(--dns-light-1) 90%, var(--dns-main) 100%);
  background-size: 100% 400%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  -webkit-text-stroke-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dns-pop {
  --dns-pop-width: calc(1.2px + 0.04em);
  --dns-pop-shift: 0.08em;
  color: #fff;
  display: inline-block;
  paint-order: stroke fill;
  position: relative;
  vertical-align: top;
  -webkit-text-stroke-width: var(--dns-pop-width);
  -webkit-text-stroke-color: var(--dns-dark-2);
  margin-bottom: calc(var(--dns-pop-width) * -1 - var(--dns-pop-shift));
  margin-inline-start: calc(var(--dns-pop-width) * -1);
  padding-bottom: calc(var(--dns-pop-width) + var(--dns-pop-shift));
  padding-inline-start: var(--dns-pop-width);
}

.dns-pop::before {
  content: attr(data-text);
  position: absolute;
  top: 0;
  bottom: calc(var(--dns-pop-shift) * -1 - var(--dns-pop-width));
  inset-inline-start: var(--dns-pop-width);
  margin-inline-start: calc(var(--dns-pop-width) * -1);
  padding-inline-start: var(--dns-pop-width);
  width: calc(100% - var(--dns-pop-width));
  z-index: -1;
  color: var(--dns-main);
  -webkit-text-stroke-width: var(--dns-pop-width);
  -webkit-text-stroke-color: transparent;
  background: linear-gradient(to bottom left, var(--dns-light-1) 0, var(--dns-light-1) 6%, var(--dns-main) 20%, var(--dns-main) 50%, var(--dns-light-1) 56%, var(--dns-main) 70%, var(--dns-main) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  background-position: 100% 0;
  background-size: 200% 200%;
  -webkit-text-fill-color: transparent;
  transform: translate3d(0, var(--dns-pop-shift), 0);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dns-gummy {
  font-kerning: none;
  font-variant-ligatures: none;
}

.dns-gummy-letter {
  display: inline-block;
  white-space: pre;
}

.dns-animated .dns-neon {
  animation: dns-neon-flicker 4s cubic-bezier(0.24, 0.31, 0.36, 0.93) forwards;
}

.dns-animated .dns-toon::before {
  animation: dns-toon 4s cubic-bezier(0.44, 0.29, 0.48, 1) forwards;
}

.dns-animated .dns-prism {
  animation: dns-prism 2s linear forwards;
}

.dns-animated .dns-gummy {
  --dns-squish-headroom: 0.12em;
  margin-block: calc(var(--dns-squish-headroom) * -1);
  padding-block: var(--dns-squish-headroom);
}

.dns-animated .dns-gummy-letter {
  animation: dns-gummy 4s cubic-bezier(0.44, 0.29, 0.48, 1) forwards;
  animation-delay: calc(var(--dns-letter-index, 0) * 50ms);
}

@keyframes dns-toon {
  0%, 5% { background-position: 50% 0; }
  55%, 100% { background-position: 50% 100%; }
}

@keyframes dns-neon-flicker {
  0%, 15%, 18%, 20%, 23%, 25%, 50% { color: #fff; }
  16%, 22%, 28% { color: var(--dns-neon-flicker); }
  51%, 100% { color: #fff; }
}

@keyframes dns-prism {
  0% { background-position-x: 0; }
  100% { background-position-x: calc(var(--dns-prism-cycle) * -1); }
}

@keyframes dns-gummy {
  0%, 35%, 100% { transform: scale(1, 1); }
  7% { transform: scale(1.28, 0.72); }
  14% { transform: scale(0.82, 1.18); }
  21% { transform: scale(1.1, 0.9); }
  28% { transform: scale(0.96, 1.04); }
}

@media (prefers-reduced-motion: reduce) {
  .dns-animated .dns-neon,
  .dns-animated .dns-toon::before,
  .dns-animated .dns-prism,
  .dns-animated .dns-gummy-letter {
    animation: none;
  }
}
</style>
